import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { DESIGN_TOKENS, getFormFieldStyles, getTypographyStyles } from '../../styles/constants';
import { GetMoreOrdersIcon, EarnMoreIcon, GrowBusinessIcon } from '../../components/shared';
import { vendorApplicationService, VendorApplicationData } from '../../services/vendorApplicationService';
import ThankYouPopup from '../../components/ThankYouPopup';
import { usePageTitle } from '../../hooks/usePageTitle';

// S3 asset URLs
const imgPattern5 = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/become-vendor-bg-pattern.png';
const logo = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

// Common country codes for phone numbers
const countryCodes = [
  { code: 'US', name: 'United States (+1)', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada (+1)', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom (+44)', flag: '🇬🇧' },
  { code: 'EG', name: 'Egypt (+20)', flag: '🇪🇬' },
  { code: 'AE', name: 'United Arab Emirates (+971)', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia (+966)', flag: '🇸🇦' },
  { code: 'KW', name: 'Kuwait (+965)', flag: '🇰🇼' },
  { code: 'QA', name: 'Qatar (+974)', flag: '🇶🇦' },
  { code: 'BH', name: 'Bahrain (+973)', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman (+968)', flag: '🇴🇲' },
  { code: 'JO', name: 'Jordan (+962)', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon (+961)', flag: '🇱🇧' },
  { code: 'FR', name: 'France (+33)', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany (+49)', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy (+39)', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain (+34)', flag: '🇪🇸' },
  { code: 'AU', name: 'Australia (+61)', flag: '🇦🇺' },
  { code: 'IN', name: 'India (+91)', flag: '🇮🇳' },
  { code: 'PK', name: 'Pakistan (+92)', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh (+880)', flag: '🇧🇩' },
];

// Form validation schema
const schema = yup.object({
  businessName: yup.string().required('Business name is required'),
  contactName: yup.string().required('Contact name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  countryCode: yup.string().required('Country code is required'),
  phone: yup.string().required('Phone number is required'),
  businessType: yup.string().required('Business type is required'),
});

type FormData = yup.InferType<typeof schema>;

const BecomeAVendor: React.FC = () => {
  usePageTitle('Become a Vendor');
  
  const [expandedFAQ, setExpandedFAQ] = useState<string | false>(false);
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      countryCode: 'QA', // Default to Qatar
    },
  });

  // React Query mutation for submitting application
  const mutation = useMutation({
    mutationFn: (data: VendorApplicationData) =>
      vendorApplicationService.submitApplication(data),
    onSuccess: (response) => {
      setShowThankYouPopup(true);
      reset(); // Reset form after successful submission
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to submit application. Please try again.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
      });
    },
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate(data);
  };

  const handleFAQChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: DESIGN_TOKENS.colors.background,
      fontFamily: DESIGN_TOKENS.typography.fontFamily
    }}>
      <Container maxWidth={false} sx={{ padding: 0, paddingRight: 0 }} disableGutters>
        <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* Left Panel - Form Section (1/3 width) */}
          <Box
            sx={{
              backgroundColor: DESIGN_TOKENS.colors.background,
              padding: { xs: 2, lg: '80px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: { xs: 'auto', lg: '100vh' },
              width: { xs: '100%', lg: '33.333%' },
              flexShrink: 0,
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  width: '120px',
                  height: '94px',
                  position: 'relative'
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Eventi Logo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            {/* Form Section */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                sx={{
                  ...getTypographyStyles('h2'),
                  mb: 3,
                }}
              >
                Submit your application now
              </Typography>

              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
                <TextField
                  {...register('businessName')}
                  fullWidth
                  label="Business Name"
                  error={!!errors.businessName}
                  helperText={errors.businessName?.message}
                  sx={{ 
                    mb: 2,
                    ...getFormFieldStyles()
                  }}
                />
                <TextField
                  {...register('contactName')}
                  fullWidth
                  label="Contact Name"
                  error={!!errors.contactName}
                  helperText={errors.contactName?.message}
                  sx={{ 
                    mb: 2,
                    ...getFormFieldStyles()
                  }}
                />
                <TextField
                  {...register('email')}
                  fullWidth
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ 
                    mb: 2,
                    ...getFormFieldStyles()
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: '0 0 120px' }}>
                    <FormControl 
                      fullWidth 
                      error={!!errors.countryCode}
                      sx={getFormFieldStyles()}
                    >
                      <InputLabel>Country</InputLabel>
                      <Select
                        {...register('countryCode')}
                        label="Country"
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{country.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors.countryCode && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.countryCode.message}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      {...register('phone')}
                      fullWidth
                      label="Phone Number"
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      placeholder="33123456"
                      sx={getFormFieldStyles()}
                    />
                  </Box>
                </Box>
                <TextField
                  {...register('businessType')}
                  fullWidth
                  label="Business Type"
                  error={!!errors.businessType}
                  helperText={errors.businessType?.message}
                  sx={{ 
                    mb: 3,
                    ...getFormFieldStyles()
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={mutation.isPending}
                  sx={{
                    backgroundColor: DESIGN_TOKENS.colors.primary,
                    borderRadius: DESIGN_TOKENS.spacing.borderRadius,
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
                    fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                    textTransform: 'none',
                    height: '56px',
                    '&:hover': {
                      backgroundColor: DESIGN_TOKENS.colors.primaryDark,
                    },
                    '&:disabled': {
                      backgroundColor: DESIGN_TOKENS.colors.textSecondary,
                      color: '#ffffff',
                    },
                  }}
                >
                  {mutation.isPending ? (
                    <>
                      <CircularProgress size={24} sx={{ color: '#ffffff', mr: 1 }} />
                      Submitting...
                    </>
                  ) : (
                    'Apply'
                  )}
                </Button>
              </Box>
            </Box>

            {/* Copyright */}
            <Typography
              sx={{
                ...getTypographyStyles('caption'),
                opacity: 0.4,
                mt: 4,
              }}
            >
              © Eventi 2025
            </Typography>
          </Box>

          {/* Right Panel - Content Sections (2/3 width) */}
          <Box
            className="right-panel"
            sx={{
              backgroundColor: DESIGN_TOKENS.colors.backgroundPaper,
              overflowY: 'auto',
              maxHeight: { xs: 'none', lg: '100vh' },
              width: { xs: '100%', lg: '66.667%' },
              flexShrink: 0,
              // Custom scrollbar styles
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: DESIGN_TOKENS.colors.backgroundPaper,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: DESIGN_TOKENS.colors.primary,
                borderRadius: '4px',
                border: `1px solid ${DESIGN_TOKENS.colors.backgroundPaper}`,
                '&:hover': {
                  background: DESIGN_TOKENS.colors.primaryDark,
                },
                '&:active': {
                  background: DESIGN_TOKENS.colors.primaryLight,
                },
              },
              // Firefox scrollbar
              scrollbarWidth: 'thin',
              scrollbarColor: `${DESIGN_TOKENS.colors.primary} ${DESIGN_TOKENS.colors.backgroundPaper}`,
            }}
          >
            {/* Hero Section */}
            <Box sx={{ 
              height: '1024px', 
              width: '100%', 
              display: 'flex', 
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              position: 'relative',
              backgroundImage: `url(${imgPattern5})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mb: 0,
              padding: '80px'
            }}>
              <Box sx={{ 
                backgroundColor: DESIGN_TOKENS.colors.primary, 
                borderRadius: DESIGN_TOKENS.spacing.borderRadius, 
                padding: '40px',
                textAlign: 'left',
                maxWidth: '604px'
              }}>
                <Typography
                  sx={{
                    ...getTypographyStyles('h1'),
                    color: DESIGN_TOKENS.colors.white,
                    mb: 2,
                  }}
                >
                  Become an Eventi Vendor
                </Typography>
                <Typography
                  sx={{
                    ...getTypographyStyles('body1'),
                    color: DESIGN_TOKENS.colors.white,
                  }}
                >
                  Reach wider customer audience, grow your business and earn more with Eventi
                </Typography>
              </Box>
            </Box>

            {/* Why Partner Section */}
            <Box sx={{ 
              padding: '120px 80px 60px 80px',
              backgroundColor: '#f4dbc5'
            }}>
              <Typography
                sx={{
                  ...getTypographyStyles('h1'),
                  mb: '80px',
                  textAlign: 'left',
                }}
              >
                Why partner with Eventi
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 5, px: { xs: 2, lg: 10 } }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GetMoreOrdersIcon />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        ...getTypographyStyles('h3'),
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      Get more orders
                    </Typography>
                    <Typography
                      sx={{
                        ...getTypographyStyles('body1'),
                        color: DESIGN_TOKENS.colors.textSecondary,
                        textAlign: 'center',
                      }}
                    >
                      Eventi helps you to reach new customers and boost your order count.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EarnMoreIcon />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        ...getTypographyStyles('h3'),
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      Earn more
                    </Typography>
                    <Typography
                      sx={{
                        ...getTypographyStyles('body1'),
                        color: DESIGN_TOKENS.colors.textSecondary,
                        textAlign: 'center',
                      }}
                    >
                      Enhancing your offerings and boost your sales revenue.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GrowBusinessIcon />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        ...getTypographyStyles('h3'),
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      Grow your business
                    </Typography>
                    <Typography
                      sx={{
                        ...getTypographyStyles('body1'),
                        color: DESIGN_TOKENS.colors.textSecondary,
                        textAlign: 'center',
                      }}
                    >
                      Access to broader audience, so you can market and grow your business
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* How It Works Section */}
            <Box sx={{ 
              padding: '60px 80px 120px 80px',
              backgroundColor: '#f4dbc5'
            }}>
              <Typography
                sx={{
                  ...getTypographyStyles('h1'),
                  mb: '80px',
                  textAlign: 'left',
                }}
              >
                How it works
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 4,
                px: { xs: 2, lg: 10 }
              }}>
                {[
                  {
                    step: '1',
                    title: 'Customer makes an order',
                    description: 'Customer places an order for specific date and time in Eventi app',
                  },
                  {
                    step: '2',
                    title: 'Your confirm',
                    description: 'Review order details and confirm the order',
                  },
                  {
                    step: '3',
                    title: 'Deliver service',
                    description: 'Prepare by the time of event and do your magic',
                  },
                  {
                    step: '4',
                    title: 'Watch your business grow',
                    description: 'Track your earnings and business performance',
                  },
                ].map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 3,
                      p: 3,
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#181818',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
                        fontSize: '20px',
                        flexShrink: 0
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          ...getTypographyStyles('h3'),
                          mb: 2,
                          color: '#181818',
                          textAlign: 'left',
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          ...getTypographyStyles('body1'),
                          color: '#666666',
                          textAlign: 'left',
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* FAQ Section */}
            <Box sx={{ 
              padding: '120px 0',
              backgroundColor: DESIGN_TOKENS.colors.primary
            }}>
              <Typography
                sx={{
                  ...getTypographyStyles('h1'),
                  color: DESIGN_TOKENS.colors.white,
                  mb: '80px',
                  textAlign: 'center',
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Box sx={{ px: { xs: 2, lg: 10 } }}>
                {[
                  {
                    question: 'Why should I become a vendor?',
                    answer: 'Registering as a vendor on the Eventi app allows you to connect with a wider audience, increase your visibility, and ultimately enhance your sales. By being listed, you can attract new customers who are actively seeking services like yours, leading to a boost in your order count.',
                    isExpanded: true
                  },
                  {
                    question: 'Is Eventi available in my country?',
                    answer: 'Eventi is currently available in multiple countries. Please check our coverage area or contact support to confirm availability in your region.',
                    isExpanded: false
                  },
                  {
                    question: 'Do you charge a commission fee?',
                    answer: 'Yes, we charge a small commission fee on successful bookings. This helps us maintain the platform and provide customer support.',
                    isExpanded: false
                  },
                  {
                    question: 'How will I receive orders from Eventi?',
                    answer: 'You will receive orders through our vendor dashboard and mobile app notifications. You can manage all your bookings in one place.',
                    isExpanded: false
                  },
                  {
                    question: 'Can I stop orders if I get too busy?',
                    answer: 'Yes, you can pause your availability or set your status to busy to temporarily stop receiving new orders.',
                    isExpanded: false
                  },
                  {
                    question: 'How do I withdraw my earnings?',
                    answer: 'You can withdraw your earnings through our secure payment system. Payments are processed within 2-3 business days.',
                    isExpanded: false
                  },
                ].map((faq, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedFAQ === `panel${index}`}
                    onChange={handleFAQChange(`panel${index}`)}
                    sx={{
                      mb: 1.5,
                      borderRadius: '16px !important',
                      backgroundColor: 'transparent',
                      color: DESIGN_TOKENS.colors.white,
                      border: expandedFAQ === `panel${index}` ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: '0 0 12px 0',
                        borderRadius: '16px !important',
                      },
                      '&:first-of-type': {
                        borderRadius: '16px !important',
                      },
                      '&:last-of-type': {
                        borderRadius: '16px !important',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          margin: '24px 0',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          ...getTypographyStyles('h3'),
                          color: DESIGN_TOKENS.colors.white,
                        }}
                      >
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ paddingTop: 0, paddingBottom: '24px' }}>
                      <Typography
                        sx={{
                          ...getTypographyStyles('body1'),
                          color: DESIGN_TOKENS.colors.white,
                        }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Thank You Popup */}
      <ThankYouPopup
        open={showThankYouPopup}
        onClose={() => setShowThankYouPopup(false)}
      />
    </Box>
  );
};

export default BecomeAVendor;
